import React from 'react';

import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { Link, useLocation } from 'react-router';

import { DropdownNavigationRoute } from './types';

interface DropdownNavigationMenuProps {
  routes: DropdownNavigationRoute[];
}

export const DropdownNavigationMenu: React.FC<DropdownNavigationMenuProps> = ({ routes }) => {
  const location = useLocation();

  const anchorEl = React.useRef<HTMLElement | null>(null);

  const selectMatchingRouteIndex = React.useCallback(
    () => {
      const routeIndex = routes.findIndex(route => route.path === location.pathname);
      return routeIndex >= 0 ? routeIndex : 0;
    },
    // routes array is not supposed to change
    // however if a parent component passes routes as a reference that is recreated over re-renders it could trigger useEffect
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname],
  );

  const [selectedRouteIndex, setSelectedRouteIndex] = React.useState(selectMatchingRouteIndex);
  const { titleContent } = routes[selectedRouteIndex] as DropdownNavigationRoute;

  React.useEffect(() => {
    setSelectedRouteIndex(selectMatchingRouteIndex());
  }, [selectMatchingRouteIndex]);

  const [isOpen, setIsOpen] = React.useState(false);
  const handleOpen = React.useCallback(() => setIsOpen(true), []);
  const handleClose = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSelectedRoute = React.useCallback(
    (routeIndex: number) => {
      setSelectedRouteIndex(routeIndex);
      handleClose();
    },
    [handleClose],
  );

  return (
    <Box display="flex" flexDirection="column" alignItems="stretch">
      <Box
        ref={anchorEl}
        onClick={handleOpen}
        display="flex"
        flexDirection="row"
        alignItems="center"
      >
        {titleContent}

        <IconButton>
          <ArrowDropDownIcon fontSize="small" />
        </IconButton>
      </Box>

      <Menu anchorEl={anchorEl.current} open={isOpen} onClose={handleClose} sx={{ mt: 1 }}>
        {routes.map((route, index) => (
          <Link
            key={route.path}
            to={route.path}
            style={{ textDecoration: 'none', color: 'initial' }}
          >
            <MenuItem onClick={() => handleSelectedRoute(index)}>
              <Typography variant="body1">{route.label}</Typography>
            </MenuItem>
          </Link>
        ))}
      </Menu>
    </Box>
  );
};
